/**
 * Staff timeclock contracts (WP4-TIMECLOCK). Routes under `/v1/timeclock`.
 *
 * Staff (`timeclock.use`) clock themselves in and out of the selected office;
 * a break is a clock-out marked `break` followed by a new clock-in. One open
 * entry per user per office.
 *
 * Managers (`timeclock.manage`) read the office timesheet (daily and weekly
 * totals in the office's timezone, overtime over 40 hours a week), add and
 * edit entries with a required reason, close entries someone forgot to clock
 * out of, and export CSV. Every manager change keeps the original values in
 * an append-only edit trail.
 */
import { z } from 'zod';

export const TIMECLOCK_PERMISSIONS = {
  use: 'timeclock.use',
  manage: 'timeclock.manage',
} as const;

/** SSE entity key; every timeclock query key starts with it, so tabs refetch. */
export const TIMECLOCK_ENTITY = 'timeclock';

/** An entry open longer than this is flagged as a probable forgotten clock-out. */
export const OPEN_FLAG_HOURS = 12;
/** Weekly hours above this are overtime. */
export const WEEKLY_OVERTIME_HOURS = 40;
/** Workweek starts on Sunday (0) in the office's timezone. */
export const WEEK_STARTS_ON = 0;
/** Longest timesheet range one request may ask for (days, before snapping to weeks). */
export const MAX_TIMESHEET_DAYS = 62;
/** Longest single entry a manager may enter. */
export const MAX_ENTRY_HOURS = 24;

const IsoInstant = z.iso.datetime({ offset: true });
export const IsoDateSchema = z.iso.date();

export const OutReasonSchema = z.enum(['end', 'break']);
export type OutReason = z.infer<typeof OutReasonSchema>;

/* ------------------------------------------------------------------------ */
/* Staff: status, clock in, clock out                                        */
/* ------------------------------------------------------------------------ */

export const TimeclockStatusSchema = z.object({
  active: z
    .object({
      id: z.uuid(),
      clockedInAt: z.string(),
      /** Open longer than OPEN_FLAG_HOURS. */
      openTooLong: z.boolean(),
      /** velora, or the PMS whose time clock has the person in (read from it). */
      source: z.string().optional(),
    })
    .nullable(),
  /** Set while the caller is out on a break started in the last OPEN_FLAG_HOURS. */
  onBreakSince: z.string().nullable(),
  /**
   * The caller's punches at this office today (office-local day), oldest
   * first: the record right after a punch (assistant role test fix 8).
   */
  today: z
    .array(
      z.object({
        clockedInAt: z.string(),
        clockedOutAt: z.string().nullable(),
        /** Why the entry closed: a break, or clocking out. */
        outReason: z.string().nullable(),
        /** velora, or the PMS the punch was read from (dentrix, opendental, eaglesoft). */
        source: z.string().optional(),
        /** The clock-in / clock-out punch in the PMS time clock (null: not sent). */
        pmsIn: z.enum(['sending', 'sent', 'failed']).nullable().optional(),
        pmsOut: z.enum(['sending', 'sent', 'failed']).nullable().optional(),
      }),
    )
    .optional(),
});
export type TimeclockStatus = z.infer<typeof TimeclockStatusSchema>;

/** Body for POST /v1/timeclock/clock-in. */
export const ClockInBody = z.object({
  note: z.string().trim().max(255).optional(),
});
export type ClockInBody = z.infer<typeof ClockInBody>;

/** Body for POST /v1/timeclock/clock-out. `break: true` starts a break. */
export const ClockOutBody = z.object({
  note: z.string().trim().max(255).optional(),
  break: z.boolean().optional(),
});
export type ClockOutBody = z.infer<typeof ClockOutBody>;

/* ------------------------------------------------------------------------ */
/* Manager: timesheet, entries, edit trail                                    */
/* ------------------------------------------------------------------------ */

export const TimesheetQuerySchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  userId: z.uuid().optional(),
});
export type TimesheetQuery = z.infer<typeof TimesheetQuerySchema>;

export const TimeEntrySchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  clockedInAt: z.string(),
  clockedOutAt: z.string().nullable(),
  outReason: OutReasonSchema.nullable(),
  note: z.string().nullable(),
  /** Added by a manager rather than clocked. */
  manual: z.boolean(),
  /** Number of manager edits in the trail. */
  editCount: z.number().int().nonnegative(),
  /** Still open and longer than OPEN_FLAG_HOURS: not counted until a manager closes it. */
  openTooLong: z.boolean(),
  /** Minutes this entry contributes to the totals (open entries count to now). */
  minutes: z.number().int().nonnegative(),
  /**
   * Closed by the office's auto clock-out (never sent to the PMS time
   * clock). `reviewed` false: "Auto clock-out, please review" for payroll.
   */
  autoOut: z
    .object({
      at: z.string(),
      rule: z.string(),
      reviewed: z.boolean(),
      reviewedBy: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type TimeEntry = z.infer<typeof TimeEntrySchema>;

export const DayTotalSchema = z.object({
  date: IsoDateSchema,
  minutes: z.number().int().nonnegative(),
});
export type DayTotal = z.infer<typeof DayTotalSchema>;

export const WeekTotalSchema = z.object({
  weekStart: IsoDateSchema,
  minutes: z.number().int().nonnegative(),
  regularMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative(),
});
export type WeekTotal = z.infer<typeof WeekTotalSchema>;

export const StaffTimesheetSchema = z.object({
  userId: z.uuid(),
  name: z.string(),
  days: z.array(DayTotalSchema),
  weeks: z.array(WeekTotalSchema),
  totalMinutes: z.number().int().nonnegative(),
  overtimeMinutes: z.number().int().nonnegative(),
  entries: z.array(TimeEntrySchema),
});
export type StaffTimesheet = z.infer<typeof StaffTimesheetSchema>;

export const TimesheetSchema = z.object({
  locationId: z.uuid(),
  timezone: z.string(),
  /** The requested range snapped out to whole workweeks. */
  from: IsoDateSchema,
  to: IsoDateSchema,
  generatedAt: z.string(),
  /** Staff with entries in the range, by name. */
  staff: z.array(StaffTimesheetSchema),
  /** Everyone active at the office, for the add-entry picker. */
  people: z.array(z.object({ userId: z.uuid(), name: z.string() })),
  /** Entries open longer than OPEN_FLAG_HOURS in the range. */
  openFlagCount: z.number().int().nonnegative(),
});
export type Timesheet = z.infer<typeof TimesheetSchema>;

export const ReasonSchema = z
  .string()
  .trim()
  .min(3, 'Give a reason of at least 3 characters.')
  .max(500);

export const CreateTimeEntryBody = z.object({
  userId: z.uuid(),
  clockedInAt: IsoInstant,
  clockedOutAt: IsoInstant,
  outReason: OutReasonSchema.optional(),
  note: z.string().trim().max(255).optional(),
  reason: ReasonSchema,
});
export type CreateTimeEntryBody = z.infer<typeof CreateTimeEntryBody>;

/** Edit or close an entry. Closing a forgotten entry is an edit that sets `clockedOutAt`. */
export const UpdateTimeEntryBody = z
  .object({
    clockedInAt: IsoInstant.optional(),
    clockedOutAt: IsoInstant.optional(),
    outReason: OutReasonSchema.optional(),
    note: z.string().trim().max(255).nullable().optional(),
    reason: ReasonSchema,
  })
  .refine(
    (b) =>
      b.clockedInAt !== undefined ||
      b.clockedOutAt !== undefined ||
      b.outReason !== undefined ||
      b.note !== undefined,
    { message: 'Change at least one field.' },
  );
export type UpdateTimeEntryBody = z.infer<typeof UpdateTimeEntryBody>;

export const TimeEntryValuesSchema = z.object({
  clockedInAt: z.string(),
  clockedOutAt: z.string().nullable(),
  outReason: OutReasonSchema.nullable(),
  note: z.string().nullable(),
});
export type TimeEntryValues = z.infer<typeof TimeEntryValuesSchema>;

export const TimeEntryEditSchema = z.object({
  id: z.uuid(),
  entryId: z.uuid(),
  /** 'delete': the entry was removed because its shift was changed in Square. */
  action: z.enum(['add', 'edit', 'close', 'delete']),
  reason: z.string(),
  /** Values before the change; null when the entry was added by a manager. */
  before: TimeEntryValuesSchema.nullable(),
  after: TimeEntryValuesSchema,
  editedBy: z.object({ userId: z.uuid().nullable(), name: z.string() }),
  editedAt: z.string(),
});
export type TimeEntryEdit = z.infer<typeof TimeEntryEditSchema>;

export const TimeEntryEditListSchema = z.object({ edits: z.array(TimeEntryEditSchema) });
export type TimeEntryEditList = z.infer<typeof TimeEntryEditListSchema>;

export const TimeEntryResultSchema = z.object({ entry: TimeEntrySchema });
export type TimeEntryResult = z.infer<typeof TimeEntryResultSchema>;

/* ------------------------------------------------------------------------ */
/* PMS sync, PIN pad, timeclock people (timeclock PMS sync)                   */
/* ------------------------------------------------------------------------ */

/** Where an entry came from: punched in Velora, or read from the office's PMS time clock. */
export const TIME_ENTRY_SOURCES = ['velora', 'dentrix', 'opendental', 'eaglesoft'] as const;
export const TimeEntrySourceSchema = z.enum(TIME_ENTRY_SOURCES);
export type TimeEntrySource = z.infer<typeof TimeEntrySourceSchema>;

export type TimeclockPms = 'dentrix' | 'opendental' | 'eaglesoft';
export const TimeclockPmsSystemSchema = z.enum(['dentrix', 'opendental', 'eaglesoft']);

const PMS_NAMES: Readonly<Record<TimeclockPms, string>> = {
  dentrix: 'Dentrix',
  opendental: 'Open Dental',
  eaglesoft: 'Eaglesoft',
};

export function timeclockPmsName(pms: TimeclockPms): string {
  return PMS_NAMES[pms];
}

/** The PMS's own time clock, as staff know it. */
export function pmsTimeClockName(pms: TimeclockPms): string {
  return pms === 'dentrix' ? 'the Dentrix Time Clock' : `the ${PMS_NAMES[pms]} time clock`;
}

export type DoInPmsAction = 'clock_in' | 'clock_out' | 'break' | 'edit_pms_entry' | 'fix_conflict';

/**
 * Why a punch was not sent to the PMS time clock:
 * - `not_yet`: the office does not send punches to this PMS (not enabled at
 *   the office, or no supported write for the PMS);
 * - `not_mapped`: Velora does not know which PMS employee the person is;
 * - `failed`: the PMS did not take it.
 */
export type PmsPunchMissReason = 'not_yet' | 'not_mapped' | 'failed';

function missSentence(name: string, why: PmsPunchMissReason): string {
  switch (why) {
    case 'not_yet':
      return `Velora does not send punches to ${name} at this office yet.`;
    case 'not_mapped':
      return `Velora does not know which ${name} employee you are yet; a manager can match you in Timesheets > Staff.`;
    case 'failed':
      return `${name} did not take the punch from Velora.`;
  }
}

/**
 * The one "Do this in <PMS>" sentence the timeclock shows when a punch is
 * not written to the PMS time clock (see PmsPunchMissReason), or for an
 * entry that came from the PMS (read-only in Velora).
 */
export function doThisInPms(
  pms: TimeclockPms,
  action: DoInPmsAction,
  why: PmsPunchMissReason = 'not_yet',
): string {
  const name = PMS_NAMES[pms];
  const clock = pmsTimeClockName(pms);
  switch (action) {
    case 'clock_in':
      return `Do this in ${name}: clock in with ${clock} too. ${missSentence(name, why)}`;
    case 'clock_out':
      return `Do this in ${name}: clock out with ${clock} too. ${missSentence(name, why)}`;
    case 'break':
      return `Do this in ${name}: clock out for your break with ${clock} too. ${missSentence(name, why)}`;
    case 'edit_pms_entry':
      return `Do this in ${name}: this punch comes from ${clock} and is read-only in Velora. Change it in ${name}.`;
    case 'fix_conflict':
      return `Do this in ${name}: to make ${name} match, change the punch in ${clock}. Velora keeps its own entry.`;
  }
}

/** The office's PMS time clock link, as the timeclock screens need it. */
export const TimeclockPmsSchema = z.object({
  system: TimeclockPmsSystemSchema,
  name: z.string(),
  /** Velora punches are written to the PMS time clock (the gate allows it). */
  writeBack: z.boolean(),
  /** Why not, staff-facing; null when writeBack. */
  writeBackReason: z.string().nullable(),
  /** PMS punches are read into this office's timesheets. */
  reading: z.boolean(),
});
export type TimeclockPmsInfo = z.infer<typeof TimeclockPmsSchema>;

/** After a punch: the "Do this in <PMS>" reminder, or null when nothing is needed. */
export const PmsReminderSchema = z
  .object({ system: TimeclockPmsSystemSchema, text: z.string() })
  .nullable();
export type PmsReminder = z.infer<typeof PmsReminderSchema>;

/** A timeclock person: a Velora user (`u:<id>`) or PIN-only staff (`s:<id>`). */
export const PersonKeySchema = z.string().regex(/^[us]:[0-9a-f-]{36}$/i, 'Unknown person.');
export type PersonKey = z.infer<typeof PersonKeySchema>;

export const PunchedViaSchema = z.enum(['session', 'pin', 'manager', 'pms']);
export type PunchedVia = z.infer<typeof PunchedViaSchema>;

export const PunchConflictSchema = z.enum([
  'times_differ',
  'open_mismatch',
  'anomaly',
  'overlaps_entries',
]);
export type PunchConflict = z.infer<typeof PunchConflictSchema>;

/** The PMS side of an entry: the punch it is, or the punch it matched. */
export const EntryPmsSchema = z.object({
  punchId: z.uuid(),
  clockIn: z.string().nullable(),
  clockOut: z.string().nullable(),
  edited: z.boolean(),
  autoOut: z.boolean(),
  /** The PMS punch disagrees with this Velora entry (not counted twice). */
  conflict: PunchConflictSchema.nullable(),
  resolution: z.enum(['keep_velora', 'use_pms']).nullable(),
});
export type EntryPms = z.infer<typeof EntryPmsSchema>;

export const SyncedTimeEntrySchema = TimeEntrySchema.extend({
  userId: z.uuid().nullable(),
  staffId: z.uuid().nullable(),
  personKey: PersonKeySchema,
  source: TimeEntrySourceSchema,
  punchedVia: PunchedViaSchema,
  /** PMS-born entries are read-only in Velora. */
  readOnly: z.boolean(),
  pms: EntryPmsSchema.nullable(),
});
export type SyncedTimeEntry = z.infer<typeof SyncedTimeEntrySchema>;

export const PmsConflictItemSchema = z.object({
  punchId: z.uuid(),
  personKey: PersonKeySchema,
  name: z.string(),
  conflict: PunchConflictSchema,
  pmsClockIn: z.string().nullable(),
  pmsClockOut: z.string().nullable(),
  pmsEdited: z.boolean(),
  entryId: z.uuid().nullable(),
  entryClockIn: z.string().nullable(),
  entryClockOut: z.string().nullable(),
});
export type PmsConflictItem = z.infer<typeof PmsConflictItemSchema>;

const MinutesBySourceSchema = z.object({
  velora: z.number().int().nonnegative(),
  dentrix: z.number().int().nonnegative(),
  opendental: z.number().int().nonnegative(),
  eaglesoft: z.number().int().nonnegative(),
});

export const SyncedStaffTimesheetSchema = StaffTimesheetSchema.extend({
  userId: z.uuid().nullable(),
  staffId: z.uuid().nullable(),
  personKey: PersonKeySchema,
  hasLogin: z.boolean(),
  entries: z.array(SyncedTimeEntrySchema),
  /** Minutes by source (counted once: a matched PMS punch adds nothing). */
  minutesBySource: MinutesBySourceSchema,
  conflictCount: z.number().int().nonnegative(),
});
export type SyncedStaffTimesheet = z.infer<typeof SyncedStaffTimesheetSchema>;

export const TimesheetPersonSchema = z.object({
  personKey: PersonKeySchema,
  userId: z.uuid().nullable(),
  staffId: z.uuid().nullable(),
  name: z.string(),
  hasLogin: z.boolean(),
});
export type TimesheetPerson = z.infer<typeof TimesheetPersonSchema>;

export const SyncedTimesheetSchema = TimesheetSchema.extend({
  staff: z.array(SyncedStaffTimesheetSchema),
  people: z.array(TimesheetPersonSchema),
  pms: TimeclockPmsSchema.nullable(),
  conflicts: z.array(PmsConflictItemSchema),
  /** PMS punches in the range whose employee is not mapped to anyone yet. */
  unmappedPunchCount: z.number().int().nonnegative(),
});
export type SyncedTimesheet = z.infer<typeof SyncedTimesheetSchema>;

/** Where a Velora punch is in the PMS time clock. */
export const PmsPunchStateSchema = z.enum(['sending', 'sent', 'failed']);
export type PmsPunchState = z.infer<typeof PmsPunchStateSchema>;

/** The caller's latest punch sent to the PMS time clock today, and how it went. */
export const PmsPunchSchema = z.object({
  commandId: z.uuid(),
  system: TimeclockPmsSystemSchema,
  action: z.enum(['clock_in', 'clock_out', 'break']),
  state: PmsPunchStateSchema,
  /** "Sent to Dentrix.", "Sending to Dentrix...", or the "Do this in <PMS>" sentence. */
  text: z.string(),
  at: z.string(),
});
export type PmsPunch = z.infer<typeof PmsPunchSchema>;

/** The status line for a punch sent to the PMS time clock. Pure. */
export function pmsPunchText(
  pms: TimeclockPms,
  action: 'clock_in' | 'clock_out' | 'break',
  state: PmsPunchState,
): string {
  const name = PMS_NAMES[pms];
  if (state === 'sent') return `Sent to ${name}.`;
  if (state === 'sending') return `Sending to ${name}...`;
  return doThisInPms(pms, action, 'failed');
}

/** Status plus the office's PMS (for the "Do this in <PMS>" reminder). */
export const SyncedTimeclockStatusSchema = TimeclockStatusSchema.extend({
  pms: TimeclockPmsSchema.nullable(),
  /** Set right after a punch when it was NOT sent: do it in the PMS too. */
  reminder: PmsReminderSchema,
  /** The latest punch sent to the PMS time clock today (null: none). */
  pmsPunch: PmsPunchSchema.nullable().optional(),
  /** Right after a punch that changed nothing, e.g. "Already clocked in in Dentrix since 7:47 AM." */
  notice: z.string().nullable().optional(),
});
export type SyncedTimeclockStatus = z.infer<typeof SyncedTimeclockStatusSchema>;

/** Manager entry add / edit: the person can be a PIN-only staff member. */
export const CreatePersonEntryBody = CreateTimeEntryBody.extend({
  userId: z.uuid().optional(),
  personKey: PersonKeySchema.optional(),
}).refine((b) => (b.userId === undefined) !== (b.personKey === undefined), {
  message: 'Pick one person.',
});
export type CreatePersonEntryBody = z.infer<typeof CreatePersonEntryBody>;

export const SyncedTimeEntryResultSchema = z.object({ entry: SyncedTimeEntrySchema });
export type SyncedTimeEntryResult = z.infer<typeof SyncedTimeEntryResultSchema>;

/* PIN pad ------------------------------------------------------------------ */

export const PIN_MIN = 4;
export const PIN_MAX = 6;
export const PinSchema = z.string().regex(/^\d{4,6}$/, `A PIN is ${PIN_MIN} to ${PIN_MAX} digits.`);

/** Failed PINs from one device before it is locked. */
export const PIN_DEVICE_MAX_FAILS = 5;
/** Failures of one PIN value (any device) before that PIN is locked at the office. */
export const PIN_VALUE_MAX_FAILS = 5;
/** Failures at the office (all devices) before the pad is locked for everyone. */
export const PIN_OFFICE_MAX_FAILS = 25;
/** The window failures are counted in, and how long a lock lasts. */
export const PIN_LOCK_MINUTES = 15;

/** A pad's own id (kiosk / browser, kept in local storage): only its hash is stored. */
export const DeviceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/, 'Bad device id.');

export const PinPunchBody = z.object({
  pin: PinSchema,
  deviceId: DeviceIdSchema,
  /** Short device label for the audit, e.g. "Front desk kiosk". */
  deviceLabel: z.string().trim().max(60).optional(),
  /** Set when the same PIN belongs to more than one person (after `choose`). */
  personKey: PersonKeySchema.optional(),
  /** `toggle` clocks in or out; `break` clocks out for a break. */
  action: z.enum(['toggle', 'break']).default('toggle'),
});
export type PinPunchBody = z.input<typeof PinPunchBody>;

/**
 * The pad's answer. A wrong PIN and a lockout are 200 answers (not errors) so
 * the attempt row and its audit event commit with the request.
 */
export const PinPunchResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('wrong_pin'),
    message: z.string(),
    attemptsLeft: z.number().int().nonnegative(),
  }),
  z.object({
    status: z.literal('locked'),
    message: z.string(),
    retryAfterSec: z.number().int().positive(),
  }),
  z.object({
    status: z.literal('choose'),
    choices: z.array(z.object({ personKey: PersonKeySchema, name: z.string() })),
  }),
  z.object({
    status: z.literal('done'),
    action: z.enum(['clocked_in', 'clocked_out', 'on_break', 'already_in']),
    name: z.string(),
    at: z.string(),
    officeName: z.string(),
    reminder: PmsReminderSchema,
    /** The punch is on its way to the PMS time clock (null: not sent). */
    pmsPunch: PmsPunchSchema.nullable().optional(),
    /** already_in: "<name> is already clocked in in Dentrix since 7:47 AM." */
    notice: z.string().nullable().optional(),
  }),
]);
export type PinPunchResult = z.infer<typeof PinPunchResultSchema>;

export const MyPinStatusSchema = z.object({
  hasPin: z.boolean(),
  setAt: z.string().nullable(),
});
export type MyPinStatus = z.infer<typeof MyPinStatusSchema>;

export const SetMyPinBody = z.object({ pin: PinSchema, password: z.string().min(1).max(200) });
export type SetMyPinBody = z.infer<typeof SetMyPinBody>;

export const SetPersonPinBody = z.object({ pin: PinSchema });
export type SetPersonPinBody = z.infer<typeof SetPersonPinBody>;

/* People and the PMS mapping (timeclock.manage) ------------------------------ */

export const TimeclockPersonSchema = z.object({
  personKey: PersonKeySchema,
  userId: z.uuid().nullable(),
  staffId: z.uuid().nullable(),
  name: z.string(),
  hasLogin: z.boolean(),
  active: z.boolean(),
  hasPin: z.boolean(),
  pinSetAt: z.string().nullable(),
  badgeId: z.string().nullable(),
  /** PMS employees mapped to this person at this office. */
  pmsEmployees: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type TimeclockPerson = z.infer<typeof TimeclockPersonSchema>;

export const TimeclockPeopleSchema = z.object({
  people: z.array(TimeclockPersonSchema),
  pms: TimeclockPmsSchema.nullable(),
});
export type TimeclockPeople = z.infer<typeof TimeclockPeopleSchema>;

export const CreateStaffBody = z.object({
  displayName: z.string().trim().min(1).max(120),
  pin: PinSchema.optional(),
  badgeId: z.string().trim().min(1).max(64).optional(),
  /** Map this PMS employee to the new person. */
  pmsEmployeeId: z.uuid().optional(),
});
export type CreateStaffBody = z.infer<typeof CreateStaffBody>;

export const UpdateStaffBody = z
  .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    active: z.boolean().optional(),
    badgeId: z.string().trim().min(1).max(64).nullable().optional(),
    /** Link this PIN-only person to a Velora user: their entries move to the user. */
    linkUserId: z.uuid().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'Change at least one field.' });
export type UpdateStaffBody = z.infer<typeof UpdateStaffBody>;

export const PmsEmployeeSchema = z.object({
  id: z.uuid(),
  pmsId: z.string(),
  name: z.string(),
  kind: z.enum(['staff', 'provider', 'employee']),
  isHidden: z.boolean(),
  lastPunchDate: z.string().nullable(),
  mapped: z
    .object({
      personKey: PersonKeySchema,
      name: z.string(),
      /** manual; provider_link / pms_user / same_name: matched automatically. */
      source: z.enum(['manual', 'provider_link', 'pms_user', 'same_name']),
    })
    .nullable(),
  ignored: z.boolean(),
  suggestion: z
    .object({
      personKey: PersonKeySchema,
      name: z.string(),
      why: z.enum(['provider_link', 'same_name']),
    })
    .nullable(),
  /** Punches waiting for this mapping. */
  waitingPunches: z.number().int().nonnegative(),
  /**
   * `time_clock`: the PMS time clock roster (PulseSync reads it); `pms_users`:
   * the PMS user list, used to send Velora punches when the roster is not read.
   */
  source: z.enum(['time_clock', 'pms_users']).optional(),
});
export type PmsEmployee = z.infer<typeof PmsEmployeeSchema>;

export const PmsEmployeesSchema = z.object({
  employees: z.array(PmsEmployeeSchema),
  pms: TimeclockPmsSchema.nullable(),
});
export type PmsEmployees = z.infer<typeof PmsEmployeesSchema>;

export const MapEmployeeBody = z.object({
  /** Who this PMS employee is in Velora; null clears the mapping. */
  personKey: PersonKeySchema.nullable(),
  /** Ignore this PMS employee (their punches are not counted). */
  ignored: z.boolean().optional(),
});
export type MapEmployeeBody = z.infer<typeof MapEmployeeBody>;

export const ResolveConflictBody = z.object({
  resolution: z.enum(['keep_velora', 'use_pms']),
  reason: ReasonSchema,
});
export type ResolveConflictBody = z.infer<typeof ResolveConflictBody>;

/* ------------------------------------------------------------------------ */
/* Auto clock-out (owner demo 2026-11-02)                                     */
/* ------------------------------------------------------------------------ */

/** Default: close an entry still open 12 hours after it started. */
export const AUTO_OUT_DEFAULT_HOURS = 12;
export const AUTO_OUT_MODES = ['after_hours', 'at_time'] as const;
export type AutoOutMode = (typeof AUTO_OUT_MODES)[number];

/**
 * Per-office auto clock-out. Off by default. A closed entry stays in Velora,
 * flagged "Auto clock-out, please review"; it is never punched to the PMS
 * time clock, even where the PMS time clock write is on.
 */
export const AutoOutSettingsSchema = z.object({
  locationId: z.uuid(),
  enabled: z.boolean(),
  mode: z.enum(AUTO_OUT_MODES),
  /** after_hours: close this many hours after the clock-in (2 to 24). */
  afterHours: z.number().int().min(2).max(24),
  /** at_time: office time "HH:MM"; the first one after the clock-in. */
  atTime: z
    .string()
    .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
    .nullable(),
  /** Add an office task for managers for each auto clock-out. */
  notifyManager: z.boolean(),
  updatedByName: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type AutoOutSettings = z.infer<typeof AutoOutSettingsSchema>;

export const AutoOutSettingsBody = z
  .object({
    enabled: z.boolean(),
    mode: z.enum(AUTO_OUT_MODES),
    afterHours: z.number().int().min(2).max(24),
    atTime: z
      .string()
      .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/)
      .nullable(),
    notifyManager: z.boolean(),
  })
  .refine((b) => b.mode !== 'at_time' || b.atTime !== null, {
    message: 'Pick the time to clock everyone out.',
    path: ['atTime'],
  });
export type AutoOutSettingsBody = z.infer<typeof AutoOutSettingsBody>;

/* ------------------------------------------------------------------------ */
/* Sprout payroll (ADR-0015): Velora's clock -> Sprout timecards             */
/* ------------------------------------------------------------------------ */

/**
 * Staff keep clocking in Velora (top bar, PIN pad); every closed shift is
 * pushed to the office's Sprout business as a timecard (POST /payroll/shifts,
 * scope timesheets). Sprout is the payroll time record; payroll itself is
 * run in Sprout by the owner (2FA). Velora never submits payroll.
 */
export const SPROUT_SHIFT_STATUSES = [
  'pending',
  'synced',
  'error',
  'changed',
  'unmapped',
  'skipped',
] as const;
export const SproutShiftStatusSchema = z.enum(SPROUT_SHIFT_STATUSES);
export type SproutShiftStatus = z.infer<typeof SproutShiftStatusSchema>;

export const SproutPayrollShiftSchema = z.object({
  id: z.uuid(),
  personKey: PersonKeySchema,
  personName: z.string(),
  startedAt: z.iso.datetime({ offset: true }),
  endedAt: z.iso.datetime({ offset: true }),
  breakMinutes: z.number().int(),
  status: SproutShiftStatusSchema,
  detail: z.string().nullable(),
  /** Approved in Sprout (or sent approved). */
  sproutApproved: z.boolean(),
  /** Sprout passed it on to payroll (Gusto). */
  sproutSynced: z.boolean(),
  syncedAt: z.iso.datetime({ offset: true }).nullable(),
});
export type SproutPayrollShift = z.infer<typeof SproutPayrollShiftSchema>;

export const SproutPayrollPersonSchema = z.object({
  personKey: PersonKeySchema,
  name: z.string(),
  hasLogin: z.boolean(),
  sproutEmployeeId: z.string().nullable(),
  sproutEmployeeName: z.string().nullable(),
  /** A Sprout employee with the same name, not linked to anyone yet. */
  suggestionId: z.string().nullable(),
});
export type SproutPayrollPerson = z.infer<typeof SproutPayrollPersonSchema>;

export const SproutPayrollSchema = z.object({
  locationId: z.uuid(),
  /** The office's Sprout key answers (payments settings hold it, sealed). */
  connected: z.boolean(),
  /** Why not, in plain words (no key, missing scope, payroll not set up in Sprout...). */
  reason: z.string().nullable(),
  mode: z.enum(['sandbox', 'live', 'simulated']).nullable(),
  businessName: z.string().nullable(),
  settings: z.object({
    syncEnabled: z.boolean(),
    sendApproved: z.boolean(),
    syncFrom: IsoDateSchema.nullable(),
    lastSyncAt: z.iso.datetime({ offset: true }).nullable(),
    lastSyncError: z.string().nullable(),
  }),
  payroll: z
    .object({
      setUp: z.boolean(),
      provider: z.string().nullable(),
      nextPeriod: z
        .object({ start: z.string(), end: z.string(), checkDate: z.string().nullable() })
        .nullable(),
    })
    .nullable(),
  /** Sprout's payroll page: the owner runs payroll there (2FA), never Velora. */
  runPayrollUrl: z.string(),
  timeUrl: z.string(),
  employees: z.array(z.object({ id: z.string(), name: z.string(), title: z.string().nullable() })),
  people: z.array(SproutPayrollPersonSchema),
  counts: z.record(SproutShiftStatusSchema, z.number().int()),
  shifts: z.array(SproutPayrollShiftSchema),
});
export type SproutPayroll = z.infer<typeof SproutPayrollSchema>;

export const SproutPayrollSettingsBody = z
  .object({
    syncEnabled: z.boolean(),
    sendApproved: z.boolean(),
    syncFrom: IsoDateSchema.nullable().optional(),
  })
  .strict();
export type SproutPayrollSettingsBody = z.infer<typeof SproutPayrollSettingsBody>;

export const SproutMapBody = z
  .object({
    personKey: PersonKeySchema,
    /** null unlinks. */
    sproutEmployeeId: z
      .string()
      .regex(/^[A-Za-z0-9_-]{4,64}$/)
      .nullable(),
  })
  .strict();
export type SproutMapBody = z.infer<typeof SproutMapBody>;

export const SproutSyncResultSchema = z.object({
  pushed: z.number().int(),
  failed: z.number().int(),
  unmapped: z.number().int(),
  changed: z.number().int(),
  waiting: z.number().int(),
});
export type SproutSyncResult = z.infer<typeof SproutSyncResultSchema>;
